import {
  HALL_OF_FAME_LIMITS,
  type HallOfFameGrade,
  type HallOfFameResponse,
} from "@core/certificates";
import { URLS } from "@core/shared/utils";
import { useIsGuest } from "@framework/account/hooks";
import { useFetcher } from "@remix-run/react";
import { useEffect, useState } from "react";

type HallOfFameLoaderData = { hallOfFame?: HallOfFameResponse | null };

function hallOfFameUrl(limit: number, grade?: number) {
  const params = new URLSearchParams({ limit: String(limit) });

  if (grade) params.set("grade", String(grade));

  return `${URLS.certificates.hallOfFame}?${params.toString()}`;
}

/**
 * Home «لوحة الشرف» carousel data, loaded after hydration so the home loader
 * never waits on it. Students get their grade; guests can switch grades.
 *
 * @example
 * const hall = useHallOfFame();
 */
export function useHallOfFame() {
  const isGuest = useIsGuest();
  const fetcher = useFetcher<HallOfFameLoaderData>();
  const [grade, setGrade] = useState<number | undefined>();
  const [grades, setGrades] = useState<HallOfFameGrade[]>([]);
  const data = fetcher.data?.hallOfFame ?? null;

  useEffect(() => {
    fetcher.load(hallOfFameUrl(HALL_OF_FAME_LIMITS.home, grade));
    // fetcher identity is stable enough; reloads are keyed on the grade.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grade]);

  useEffect(() => {
    // Keep the chips of the unfiltered answer while a grade is selected.
    if (data?.grades?.length) setGrades(data.grades);
  }, [data]);

  const certificates = data?.certificates ?? [];

  return {
    isGuest,
    certificates,
    grades: isGuest ? grades : [],
    grade,
    selectGrade: setGrade,
    isLoading: fetcher.state !== "idle",
    // Hide the whole section when there is nothing to celebrate.
    isHidden: !data || (!grade && certificates.length === 0),
    viewAllUrl: `${URLS.certificates.hallOfFame}${grade ? `?grade=${grade}` : ""}`,
  };
}
